import { useCallback, useRef } from 'react';
import { CommonActions } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { APP_STACK_NAVIGATOR_ID } from '../../AppNavigation.constants';
import type { AppStackParamList } from '../../AppNavigation.props';

/**
 * Returns a helper function that redirects to the corresponding screen provided
 * as a route for `Auth` related screens.
 */
export const useRedirectAfterAuthStateChange = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const navigation =
    useSafeNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const router = useSafeRouter();
  const { redirect: redirectToPath = 'home', redirectToGiftCardId } =
    useSafeRouteParams<{ redirect?: string; redirectToGiftCardId?: string }>();

  // ─── RN Navigation Redirects ──────────────────────────────────────────

  const { current: redirects } = useRef<RedirectHandlers>({
    home() {
      navigation?.navigate('MainTabs', {
        screen: 'HomeTab',
        params: { screen: 'Home' },
        initial: false,
      });
    },
    locations() {
      navigation?.navigate('MainTabs', {
        screen: 'MenuTab',
        params: { screen: 'Locations' },
      });
    },
    profile() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Profile', initial: false },
      });
    },
    orders() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Orders', initial: false },
      });
    },
    addresses() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Addresses', initial: false },
      });
    },
    favorites() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Favorites', initial: false },
      });
    },
    'payment-and-gift-cards'() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: {
          screen: 'PaymentAndGiftCards',
          initial: false,
          params: { giftCardId: redirectToGiftCardId },
        },
      });
    },
    'credit-and-promo-codes'() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'CreditAndPromoCodes', initial: false },
      });
    },
    'dietary-restrictions'() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'AccountDietaryRestrictions', initial: false },
      });
    },
    bag() {
      navigation?.navigate('Modal', {
        screen: 'Bag',
      });
    },
    'add-gift-card'() {
      navigation?.navigate('Modal', {
        screen: 'AddGiftCard',
        params: { giftCardId: redirectToGiftCardId },
      });
    },
    'gift-cards'() {
      navigation?.navigate('MainTabs', {
        screen: 'GiftCardsTab',
        params: { screen: 'GiftCards' },
        initial: true,
      });
    },
    'gift-redeem'() {
      if (!redirectToGiftCardId) return;

      navigation?.navigate('MainTabs', {
        screen: 'GiftCardsTab',
        params: {
          screen: 'GiftCardRedemption',
          params: { giftCardId: redirectToGiftCardId },
        },
        initial: false,
      });
    },
    notifications() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Notifications', initial: false },
      });
    },
    payments() {
      navigation?.navigate('Modal', {
        screen: 'Payments',
        params: {},
      });
    },
  });

  /**
   * Dismisses `Auth` stack after auth state change (RN navigation only)
   */
  const dismissAuthStack = useCallback(() => {
    const appStackNavigator = navigation?.getParent(
      APP_STACK_NAVIGATOR_ID as never,
    );

    appStackNavigator?.dispatch((state) => {
      const routesWithoutAuth = withoutAuthStack(state.routes);

      return CommonActions.reset({
        ...state,
        routes: routesWithoutAuth,
        index: routesWithoutAuth.length - 1,
      });
    });
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  const redirectHandler = redirects[redirectToPath];

  return useCallback(() => {
    if (isExpoRouterEnabled) {
      // Guard: gift-redeem requires a gift card ID.
      if (redirectToPath === 'gift-redeem' && !redirectToGiftCardId) return;

      const pathname = EXPO_ROUTER_REDIRECTS[redirectToPath] ?? '/';
      const params = getGiftCardParams(redirectToPath, redirectToGiftCardId);

      // NOTE: Defer navigation to ensure the Root Layout is fully mounted.
      //       Without this, navigation throws when triggered from a
      //       `useEffect` that fires before the navigator is ready.
      requestIdleCallback(() => {
        if (router?.canDismiss()) {
          router?.dismissAll();
        }

        requestIdleCallback(() => {
          router?.navigate(params ? { pathname, params } : pathname);
        });
      });
      return;
    }

    redirectHandler?.();
    dismissAuthStack();
  }, [
    isExpoRouterEnabled,
    router,
    redirectToPath,
    redirectToGiftCardId,
    dismissAuthStack,
    redirectHandler,
  ]);
};

// ─── Expo Router Redirect Map ─────────────────────────────────────────────────

const EXPO_ROUTER_REDIRECTS: Record<string, string> = {
  home: '/',
  locations: '/locations',
  profile: '/account/profile',
  orders: '/account/orders',
  addresses: '/account/addresses',
  favorites: '/account/favorites',
  'payment-and-gift-cards': '/account/payment-and-gift-cards',
  'credit-and-promo-codes': '/account/credit-and-promo-codes',
  'dietary-restrictions': '/account/dietary-restrictions',
  bag: '/bag',
  'add-gift-card': '/account/add-gift-card',
  'gift-cards': '/gift',
  'gift-redeem': '/gift/redeem',
  notifications: '/account/notifications',
  payments: '/payments',
};

const GIFT_CARD_REDIRECTS = new Set([
  'payment-and-gift-cards',
  'add-gift-card',
  'gift-redeem',
]);

// ─── Utils ───────────────────────────────────────────────────────────────────

/**
 * Dismisses `Auth` stack from `MainTabs` stack
 */
function withoutAuthStack(routes: readonly NavigationRoute[]) {
  return routes.filter((route) => route.name !== 'Auth');
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getGiftCardParams(
  redirectToPath: string,
  redirectToGiftCardId: string | undefined,
): Record<string, string> | undefined {
  if (!redirectToGiftCardId) return;
  if (!GIFT_CARD_REDIRECTS.has(redirectToPath)) return;

  return { giftCardId: redirectToGiftCardId };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type RedirectHandlers = Record<string, () => void>;

type NavigationRoute = Readonly<{
  name: string;
}>;
